import { useNavigate } from 'react-router-dom';
import moment from 'moment';

import { Box, Button, Drawer, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import { ActivityEvent, ActivityFieldChange } from '../../api/activityApi/activityApi';
import {
  APP_LABELS,
  CATEGORY_LABELS,
  activityTitle,
  changeFieldLabel,
  changeValue,
  entityName,
  formatFieldValue,
  isFailure,
} from './activityHelpers';
import { useActivityStyles } from './activityStyles';

interface ActivityEventDrawerProps {
  event: ActivityEvent | null;
  onClose: () => void;
  /** Hidden on the person timeline, which is where the link leads. */
  showPersonLink?: boolean;
}

/** Details of one event for firm admins: what changed, before and after. No device or technical data. */
const ActivityEventDrawer = ({
  event,
  onClose,
  showPersonLink = true,
}: ActivityEventDrawerProps) => {
  const { classes } = useActivityStyles();
  const navigate = useNavigate();

  const renderValue = (change: ActivityFieldChange, side: 'old' | 'new') => {
    // Not stored, unless the app sent it masked (e.g. an account number: ****1234).
    if (change.policy === 'OMIT' && change.oldLabel === null && change.newLabel === null) {
      return <span className={classes.omitted}>not stored</span>;
    }
    const value = changeValue(change, side);
    if (value === null) return <span className={classes.omitted}>empty</span>;
    return formatFieldValue(value);
  };

  return (
    <Drawer anchor="right" open={!!event} onClose={onClose}>
      {event && (
        <Box className={classes.drawer}>
          <Box className={classes.drawerHeader}>
            <Box>
              <Typography className={classes.personName}>
                {activityTitle(event)}
                {isFailure(event) && <span className={classes.failedBadge}>FAILED</span>}
              </Typography>
              <Typography className={classes.subtitle}>
                {moment(event.occurredAt).format('MMM D, YYYY h:mm:ss A')}
              </Typography>
            </Box>
            <IconButton onClick={onClose} sx={{ color: 'inherit' }} aria-label="Close">
              <CloseIcon />
            </IconButton>
          </Box>

          <Box>
            <dl className={classes.details}>
              <dt>By</dt>
              <dd>{event.actorName}</dd>
              <dt>Where</dt>
              <dd>{APP_LABELS[event.app ?? ''] ?? '—'}</dd>
            </dl>
            {showPersonLink && (
              <Button
                size="small"
                sx={{ mt: 1, px: 0, textTransform: 'none', color: '#9CB0C1' }}
                onClick={() =>
                  navigate(`/activity/${event.actorType}/${encodeURIComponent(event.actorId)}`)
                }
              >
                View all activity by {event.actorName}
              </Button>
            )}
          </Box>

          <Box>
            <dl className={classes.details}>
              <dt>Record</dt>
              <dd>{event.subjectLabel ?? '—'}</dd>
              <dt>Type</dt>
              <dd>{entityName(event.subjectType)}</dd>
              <dt>Category</dt>
              <dd>{CATEGORY_LABELS[event.category] ?? event.category}</dd>
              <dt>Matter</dt>
              <dd>{event.matter ? `#${event.matter.referenceId}` : '—'}</dd>
            </dl>
          </Box>

          <Box>
            <Box className={classes.sectionTitle} sx={{ fontWeight: 'bold' }}>
              {event.changes.length === 1
                ? '1 field changed'
                : `${event.changes.length} fields changed`}
            </Box>
            {event.changes.length === 0 ? (
              <Typography className={classes.subtitle}>No field changes were recorded.</Typography>
            ) : (
              <table className={classes.changesTable}>
                <thead>
                  <tr>
                    <th>Field</th>
                    <th>Before</th>
                    <th>After</th>
                  </tr>
                </thead>
                <tbody>
                  {event.changes.map((change) => (
                    <tr key={change.field}>
                      <td>{changeFieldLabel(change)}</td>
                      <td>{renderValue(change, 'old')}</td>
                      <td>{renderValue(change, 'new')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Box>
        </Box>
      )}
    </Drawer>
  );
};

export default ActivityEventDrawer;
